import {
  FormControl,
  FormLabel,
  Flex,
  Collapse,
  Box,
  Text,
  Link,
  RadioGroup,
  Radio,
  FormErrorMessage,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { useAppSelector, useAppDispatch } from '../../../../store/redux/hooks';
import React, {
  useMemo,
  useState,
  useRef,
  useCallback,
  RefObject,
} from 'react';
import {
  removeFormField,
  setFormFieldItemProp,
  setFormFieldLinkToTemplateField,
} from '../../../../store/redux/slices/form.slice';
import { clearFormFieldErrors } from '../../../../store/redux/slices/templateErrors.slice';
import { useTemplateFieldValidation } from '../validation/useTemplateFieldValidation';
import { FormFieldState } from '../../../../store/types/form';
import { CustomErrorIcon } from '../../../../theme/icons/CustomErrorIcon';
import { getTemplateFieldDisplayNameFromId } from '../utils/fieldHelpers';
import InputFormField from './InputFormField';
import DropdownFormField from './DropdownFormField';
import DateFormField from './DateFormField';
import FieldEditIndicator from '../FieldEditIndicator';
import { useFieldEditIndicator } from '../hooks/useFieldEditIndicator';
import { getFormFieldError } from '../validation/validationUtils';

const FormField = ({
  field,
  index,
  isDisabled = false,
  isTypeLockedToDropdown = false,
  defaultOpen = false,
}: {
  field: FormFieldState;
  index: number;
  isDisabled?: boolean;
  isTypeLockedToDropdown?: boolean;
  defaultOpen?: boolean;
}) => {
  const { handleFocus, validateSection } = useTemplateFieldValidation('form');
  const dispatch = useAppDispatch();
  const form = useAppSelector((s) => s.form);
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const templateFields = useAppSelector((s) => s.template.fields);
  const formFields = useAppSelector((s) => s.form.formFields);
  const errors = useAppSelector((s) => s.templateErrors);

  const getError = (path: string) => getFormFieldError(errors, path);
  const fieldErrorPrefix = `form.formFields.${index}`;
  const fieldError =
    getError(`${fieldErrorPrefix}.id`) ??
    getError(`${fieldErrorPrefix}.label`) ??
    getError(`${fieldErrorPrefix}.name`) ??
    getError(`${fieldErrorPrefix}.type`) ??
    getError(`${fieldErrorPrefix}.placeholder`) ??
    getError(`${fieldErrorPrefix}.options`);

  const templateFieldOptions = useMemo(() => {
    const passFields = (
      Object.values(templateFields).flatMap((section) =>
        Object.values(section ?? {})
      ) as Array<{ id?: string; label?: string } | null>
    )
      .filter((f): f is { id: string; label: string } => f != null && !!f.id)
      .map((f) => ({ id: f.id, label: f.label }));
    return field.type === 'dropdown'
      ? [...passFields, { id: 'none', label: 'Group tag' }]
      : passFields;
  }, [templateFields, field.type]);

  const selectedByOtherFormFields = useMemo(
    () =>
      new Set(
        formFields
          .filter((_, i) => i !== index)
          .map((f) => f.id)
          .filter(Boolean)
      ),
    [formFields, index]
  );

  const fieldId = field._clientId ?? field.id;

  const markTouched = useCallback(
    (fieldKey: 'id' | 'label' | 'pattern' | 'message') => {
      if (fieldKey === 'id') {
        dispatch(
          setFormFieldItemProp({
            fieldId,
            key: '_idTouched',
            value: true,
          })
        );
      } else if (fieldKey === 'label') {
        dispatch(
          setFormFieldItemProp({
            fieldId,
            key: '_labelTouched',
            value: true,
          })
        );
      } else {
        dispatch(
          setFormFieldItemProp({
            fieldId,
            key: 'validation',
            value: {
              ...(field.validation ?? {
                name: 'none',
                pattern: '',
                message: '',
              }),
              ...(fieldKey === 'pattern'
                ? { _patternTouched: true }
                : { _messageTouched: true }),
            },
          })
        );
      }
    },
    [dispatch, fieldId, field.validation]
  );

  const handleConnectToPassField = (selectedId: string) => {
    markTouched('id');
    const normalizedId = selectedId === 'Group tag' ? 'none' : selectedId;
    const templateField = templateFieldOptions.find(
      (t) => t.id === normalizedId
    );
    if (templateField) {
      const name =
        getTemplateFieldDisplayNameFromId(selectedId) || templateField.label;
      dispatch(
        setFormFieldLinkToTemplateField({
          fieldId,
          templateFieldId: templateField.id,
          templateFieldLabel: templateField.label,
          templateFieldName: name,
        })
      );
      const updatedFormFields = form.formFields.map((f, i) =>
        i === index
          ? {
              ...f,
              id: templateField.id,
              label:
                templateField.id === 'none' ? f.label : templateField.label,
              name,
            }
          : f
      );
      validateSection({ ...form, formFields: updatedFormFields });
    }
  };

  const formFieldRef = useRef<HTMLDivElement>(null);
  const typeRef = useRef<HTMLDivElement>(null);
  const idRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);
  const validationTypeRef = useRef<HTMLDivElement>(null);
  const requiredRef = useRef<HTMLDivElement>(null);
  const extIdRef = useRef<HTMLDivElement>(null);
  const placeholderRef = useRef<HTMLDivElement>(null);
  const validationPatternRef = useRef<HTMLDivElement>(null);
  const validationMessageRef = useRef<HTMLDivElement>(null);
  const dateFormatRef = useRef<HTMLDivElement>(null);
  const optionRefsMap = useRef<
    Record<string, RefObject<HTMLDivElement | null>>
  >({});

  const getOptionRef = useCallback(
    (optionId: string): RefObject<HTMLDivElement> => {
      if (!optionRefsMap.current[optionId]) {
        optionRefsMap.current[optionId] = React.createRef();
      }
      return optionRefsMap.current[optionId] as RefObject<HTMLDivElement>;
    },
    []
  );

  const fieldRefMap = useMemo(() => {
    const map: Record<string, RefObject<HTMLDivElement>> = {
      [`form.formFields.${index}.type`]: typeRef,
      [`form.formFields.${index}.id`]: idRef,
      [`form.formFields.${index}.label`]: labelRef,
      [`form.formFields.${index}.required`]: requiredRef,
      [`form.formFields.${index}.extId`]: extIdRef,
      [`form.formFields.${index}.validation.name`]: validationTypeRef,
      [`form.formFields.${index}.dateFormat`]: dateFormatRef,
      [`form.formFields.${index}.placeholder`]: placeholderRef,
      [`form.formFields.${index}.validation.pattern`]: validationPatternRef,
      [`form.formFields.${index}.validation.message`]: validationMessageRef,
    };
    if (field.id) {
      map[`fields.${field.id}.label`] = labelRef;
      map[`fields.${field.id}.placeholder`] = placeholderRef;
      map[`fields.${field.id}.validation.pattern`] = validationPatternRef;
      map[`fields.${field.id}.validation.message`] = validationMessageRef;
      field.options?.forEach((opt) => {
        map[`fields.${field.id}.options.${opt.id}.label`] = getOptionRef(
          opt.id
        );
      });
    }
    field.options?.forEach((opt) => {
      map[`form.formFields.${index}.options.${opt.id}.label`] = getOptionRef(
        opt.id
      );
    });
    return map;
  }, [index, field.id, field.options, getOptionRef]);
  const { activeFieldRef, isFieldBeingEdited } = useFieldEditIndicator({
    fieldRefMap,
  });

  return (
    <FieldEditIndicator
      fieldRef={formFieldRef}
      isFieldBeingEdited={isFieldBeingEdited}
      activeFieldRef={activeFieldRef}
      left="50px"
      top="72px"
    >
      <Box
        id={`form-field-${index}`}
        p={3}
        border="1px solid"
        borderColor={fieldError ? 'red.500' : 'primaryBackground'}
        borderRadius="md"
        mb={3}
        position="relative"
        opacity={isDisabled ? 0.6 : 1}
        pointerEvents={isDisabled ? 'none' : 'auto'}
      >
        <FormControl isInvalid={!!fieldError}>
          <Flex
            align="center"
            justify="space-between"
            cursor="pointer"
            onClick={() => setIsOpen(!isOpen)}
          >
            <Flex alignItems="center" gap={2}>
              <FormLabel variant="templateBuilderLabel" m={0} fontWeight="500">
                Form field {index + 1}
              </FormLabel>
              {field.id && (
                <Text fontSize="14px" fontWeight="600">
                  | {field.id}
                </Text>
              )}
            </Flex>

            {isOpen ? (
              <ChevronUpIcon boxSize="20px" />
            ) : (
              <ChevronDownIcon boxSize="20px" />
            )}
          </Flex>
          <Collapse in={isOpen} animateOpacity>
            <Flex direction="column">
              <FieldEditIndicator
                fieldRef={typeRef}
                isFieldBeingEdited={isFieldBeingEdited}
                activeFieldRef={activeFieldRef}
                left="-55px"
                top="35px"
              >
                <FormControl
                  mt={6}
                  isInvalid={!!getError(`${fieldErrorPrefix}.type`)}
                >
                  <FormLabel variant="templateBuilderLabel">
                    Field type
                  </FormLabel>
                  <RadioGroup
                    display="flex"
                    flexDirection="column"
                    gap={4}
                    mt={4}
                    ml={4}
                    value={field.type || undefined}
                    onChange={(value) => {
                      handleFocus(`form.formFields.${index}.type`);
                      dispatch(
                        setFormFieldItemProp({
                          fieldId: field._clientId ?? field.id,
                          key: 'type',
                          value: value as 'input' | 'dropdown' | 'date',
                        })
                      );
                      const updatedFormFields = form.formFields.map((f, i) => {
                        if (i !== index) return f;
                        const updated = {
                          ...f,
                          type: value as 'input' | 'dropdown' | 'date',
                        };
                        if (value === 'dropdown') {
                          updated.placeholder = 'Select an option...';
                        }
                        return updated;
                      });
                      validateSection({
                        ...form,
                        formFields: updatedFormFields,
                      });
                    }}
                  >
                    <Radio
                      value="input"
                      isDisabled={isTypeLockedToDropdown}
                      onFocus={() =>
                        handleFocus(`form.formFields.${index}.type`)
                      }
                      onClick={() =>
                        handleFocus(`form.formFields.${index}.type`)
                      }
                    >
                      Input text
                    </Radio>
                    <Radio
                      value="dropdown"
                      onFocus={() =>
                        handleFocus(`form.formFields.${index}.type`)
                      }
                      onClick={() =>
                        handleFocus(`form.formFields.${index}.type`)
                      }
                    >
                      Dropdown menu
                    </Radio>
                    <Radio
                      value="date"
                      isDisabled={isTypeLockedToDropdown}
                      onFocus={() =>
                        handleFocus(`form.formFields.${index}.type`)
                      }
                      onClick={() =>
                        handleFocus(`form.formFields.${index}.type`)
                      }
                    >
                      Date
                    </Radio>
                  </RadioGroup>
                  {getError(`${fieldErrorPrefix}.type`) && (
                    <FormErrorMessage
                      pl={2}
                      mt={4}
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                    >
                      <CustomErrorIcon />
                      {getError(`${fieldErrorPrefix}.type`)}
                    </FormErrorMessage>
                  )}
                </FormControl>
              </FieldEditIndicator>
              <Collapse in={field.type === 'input'} animateOpacity>
                <InputFormField
                  fieldData={field}
                  fieldIndex={index}
                  templateFieldOptions={templateFieldOptions}
                  selectedByOtherFormFields={selectedByOtherFormFields}
                  handleConnectToPassField={handleConnectToPassField}
                  handleFocus={handleFocus}
                  validateSection={validateSection}
                  form={form}
                  markTouched={markTouched}
                  fieldRefs={{
                    idRef,
                    labelRef,
                    validationTypeRef,
                    requiredRef,
                    extIdRef,
                    validationPatternRef,
                    validationMessageRef,
                  }}
                  activeFieldRef={activeFieldRef}
                  isFieldBeingEdited={isFieldBeingEdited}
                />
              </Collapse>
              <Collapse in={field.type === 'dropdown'} animateOpacity>
                <DropdownFormField
                  fieldData={field}
                  fieldIndex={index}
                  templateFieldOptions={templateFieldOptions}
                  selectedByOtherFormFields={selectedByOtherFormFields}
                  handleConnectToPassField={handleConnectToPassField}
                  handleFocus={handleFocus}
                  validateSection={validateSection}
                  form={form}
                  markTouched={markTouched}
                  fieldRefs={{
                    idRef,
                    labelRef,
                    placeholderRef,
                    requiredRef,
                    getOptionRef,
                  }}
                  activeFieldRef={activeFieldRef}
                  isFieldBeingEdited={isFieldBeingEdited}
                />
              </Collapse>
              <Collapse in={field.type === 'date'} animateOpacity>
                <DateFormField
                  fieldData={field}
                  fieldIndex={index}
                  templateFieldOptions={templateFieldOptions}
                  selectedByOtherFormFields={selectedByOtherFormFields}
                  handleConnectToPassField={handleConnectToPassField}
                  handleFocus={handleFocus}
                  validateSection={validateSection}
                  form={form}
                  markTouched={markTouched}
                  fieldRefs={{
                    idRef,
                    labelRef,
                    dateFormatRef,
                    requiredRef,
                  }}
                  activeFieldRef={activeFieldRef}
                  isFieldBeingEdited={isFieldBeingEdited}
                />
              </Collapse>
            </Flex>
            <Flex mt={4} alignItems="center" justify="flex-end" gap={2}>
              <Link
                variant="orange"
                fontSize="14px"
                onClick={() => {
                  dispatch(clearFormFieldErrors(index));
                  dispatch(removeFormField(field._clientId ?? field.id));
                }}
              >
                Remove field
              </Link>
            </Flex>
          </Collapse>
        </FormControl>
      </Box>
    </FieldEditIndicator>
  );
};

export default FormField;
